import { useState } from 'react'
import type { RefObject } from 'react'
import { HintDot } from '../help/HintDot'
import { useT } from '../i18n'
import type { MapCanvasHandle } from './MapCanvas'
import { exportFileName, type ExportContext } from './exportPlate'
import { download, exportPng, framePngBytes } from './exportImage'
import { runSeries, type SeriesDecision, type SeriesResult, type SeriesRunner } from './exportSeries'
import { seriesButtonState } from './seriesButton'
import { SeriesPlanDialog } from './SeriesPlanDialog'
import type { SeriesPlan } from './seriesPlan'
import { exportReadyState, type LayerShowState } from './exportReady'
import { graticuleFrame } from './graticule'
import { maxLinesFor, useGraticuleDensity } from './graticuleDensity'
import {
  exportMp4,
  exportNote,
  videoPlan,
  videoSupported,
  type Container,
  type ExportAnimation,
  type ExportNote,
} from './exportVideo'

export type { ExportAnimation }
export type { SeriesRunner }

/**
 * Скачивание текущего вида: PNG кадра и MP4 с анимацией усиков.
 *
 * Запись сама ведёт анимацию по точным фазам цикла (см. exportVideo) и сама
 * возвращает экран как было — от человека не требуется ни включать анимацию, ни
 * замирать над картой. Кнопка показывает долю готовности: молчащая кнопка на
 * пару секунд читается как «не нажалось».
 *
 * Если записывать нечего или браузер не умеет кодировать видео — кнопка гаснет
 * и говорит причину. Пустого файла она не отдаёт: статичный ролик получатель
 * прочитает как поломку, а не как «анимации не было».
 *
 * Третья кнопка — «Серия»: пять кадров одного вида (снос, апвеллинг, комфортная
 * среда, зоопланктон, батиметрия) одним архивом, вместе с файлом параметров.
 * Состав кадров зависит от вида и живёт в реестре (`seriesCatalog`), поэтому для
 * вида, которого там ещё нет, кнопка гаснет и говорит об этом — прятать её
 * нельзя по той же причине, по какой не прячут MP4: невидимая кнопка не
 * объясняет, почему её нет.
 */
export function ExportViewButton({
  mapRef,
  info,
  animation,
  series,
  layerState = 'ready',
}: {
  mapRef: RefObject<MapCanvasHandle | null>
  info: ExportContext
  animation?: ExportAnimation
  /** Нет пропа — раздел серию не снимает (см. `seriesHint`). */
  series?: SeriesRunner
  /** Что сейчас со слоем на карте. Кадр без данных наружу не отдаём. */
  layerState?: LayerShowState
}) {
  const t = useT()
  const [busy, setBusy] = useState<'png' | 'mp4' | 'series' | null>(null)
  const [share, setShare] = useState(0)
  const [note, setNote] = useState<ExportNote>(null)
  // Техническая причина отказа. Показывается рядом с человеческой фразой, чтобы
  // её можно было переслать: у чужого браузера консоли мы не видим, а без
  // причины «не удалось» неотличимо ни от чего (см. exportVideo.failureDetail).
  const [detail, setDetail] = useState<string | null>(null)
  // Чем записали на самом деле. Кнопка называется MP4, а на браузере без H.264
  // файл выходит .webm — умолчать значило бы оставить человека с файлом, которого
  // он не просил, и без объяснения, почему он не открывается в его плеере.
  const [container, setContainer] = useState<Container | null>(null)
  // Какие сутки попали в файл. Окно записи короче окна перемотки, когда слоя на
  // какие-то даты нет ни в тайлах, ни в базе, — умолчав об этом, мы оставили бы
  // человека в уверенности, что ролик накрыл всё (см. ExportAnimation).
  const [covered, setCovered] = useState<string | null>(null)
  // Какой кадр серии снимается сейчас: молчащая кнопка на полминуты читается
  // как «зависло», а «Кадр 3 из 5» — как работа.
  const [frameAt, setFrameAt] = useState(0)
  const [seriesNote, setSeriesNote] = useState<SeriesResult | null>(null)
  // Сколько кадров реально уехало в архив. Число кадров реестра тут не годится:
  // кадр без данных в архив не идёт, и «Сохранено кадров: 5» было бы неправдой.
  const [seriesCount, setSeriesCount] = useState(0)
  // План, о котором спрашиваем человека, и обещание, которое ждёт его ответа.
  const [askPlan, setAskPlan] = useState<SeriesPlan | null>(null)
  const [answer, setAnswer] = useState<((d: SeriesDecision) => void) | null>(null)
  // Куда ушло время записи. Показывается ВСЕГДА, а не только при отказе: ролик,
  // писавшийся четыре минуты, — вопрос сам по себе, и ответить на него по одной
  // полоске нечем. Строка техническая (как `detail`), в каталог не идёт.
  const [timing, setTiming] = useState<string | null>(null)

  const canvas = () => mapRef.current?.getMap()?.getCanvas() ?? null
  const plan = videoPlan(animation, videoSupported())
  const maxLines = maxLinesFor(useGraticuleDensity())

  // Координатная шкала на экране нарисована в DOM поверх карты, в снимок
  // WebGL-полотна она не попадает — снимаем её отдельно, тем же расчётом, что
  // и на экране (graticule.ts), и дорисовываем на кадр. Густота — та же, что
  // выбрана в настройках: скачанный кадр обязан совпадать с тем, что человек
  // видел, когда жал кнопку.
  const grid = () => {
    const map = mapRef.current?.getMap()
    return map ? graticuleFrame(map, maxLines) : null
  }

  const savePng = async () => {
    const c = canvas()
    if (!c || busy || !ready.ok) return
    setBusy('png')
    setNote(null)
    try {
      await exportPng(c, info, exportFileName(info, 'png'), grid())
    } catch {
      setNote('failed')
    } finally {
      setBusy(null)
    }
  }

  const saveMp4 = async () => {
    if (!canvas() || busy || !plan.record || !animation) return
    setBusy('mp4')
    setNote(null)
    setDetail(null)
    setContainer(null)
    setCovered(null)
    setTiming(null)
    setShare(0)
    try {
      const result = await exportMp4(
        canvas,
        info,
        exportFileName(info, 'mp4'),
        animation,
        setShare,
        grid(),
        setDetail,
        setContainer,
        summary => {
          setTiming(summary)
          // Дублируем в консоль: строку под кнопкой человек перешлёт скриншотом,
          // а из консоли её можно скопировать целиком.
          console.info('[export] ' + summary)
        },
      )
      // Разные причины — разные слова: «ничего не двигалось» и «браузер не берёт
      // H.264» это не то же, что «кодировщик отвалился посреди записи». Только у
      // последнего повтор осмыслен (см. exportNote).
      setNote(exportNote(result))
      if (result === 'saved') setCovered(animation.coverageLabel?.() ?? null)
    } catch {
      setNote('failed')
    } finally {
      setBusy(null)
    }
  }

  const saveSeries = async () => {
    if (!canvas() || busy || !series?.ready) return
    setBusy('series')
    setNote(null)
    setSeriesNote(null)
    setShare(0)
    setFrameAt(0)
    setSeriesCount(0)
    const grid0 = grid()
    let shot = 0
    try {
      const result = await runSeries(series, exportFileName(info, 'zip', 'series'), {
        // Кадр собирается тем же путём, что одиночный PNG: плашка и координатная
        // сетка на кадре серии обязаны выглядеть ровно так же.
        capture: frameInfo => {
          const c = canvas()
          if (!c) throw new Error('series: map canvas is gone')
          shot++
          return framePngBytes(c, frameInfo, grid0)
        },
        // Выбор человека: дорисовать недостающее или взять готовое. Окно
        // показывается ТОЛЬКО когда есть о чём спрашивать (см. exportSeries).
        decide: p => new Promise<SeriesDecision>(resolve => {
          setAskPlan(p)
          // Функцию в состояние кладём обёрткой: setState(fn) означал бы
          // «посчитай новое значение», а не «запомни эту функцию».
          setAnswer(() => (d: SeriesDecision) => {
            setAskPlan(null)
            setAnswer(null)
            resolve(d)
          })
        }),
        save: (data, name) => download(new Blob([data], { type: 'application/zip' }), name),
        // Данные кадра собирает СЕРВЕР: они лежат в его базе, а в браузере нет
        // ни кодировщика NetCDF, ни самих полей на всех горизонтах.
        dataset: series.dataset,
        onProgress: setShare,
        onFrame: index => setFrameAt(index),
      })
      setSeriesCount(shot)
      setSeriesNote(result)
    } catch {
      setSeriesNote('failed')
    } finally {
      setBusy(null)
    }
  }

  // Кадр не отдаётся, пока под ним нет данных: проверить картинку нечем, а
  // пустое поле на ней неотличимо от честного «здесь ничего нет».
  const ready = exportReadyState(layerState)
  const seriesState = seriesButtonState(series, busy !== null)
  const seriesHint = seriesState.hintKey ? t(seriesState.hintKey) : undefined

  const mp4Hint =
    plan.record
      ? undefined
      : plan.reason === 'unsupported'
        ? t('map.export.mp4Unsupported')
        : t('map.export.mp4NoMotion')

  return (
    <div className="space-y-1.5">
      <div className="eyebrow flex items-center gap-1.5">
        {t('map.export.title')}
        <HintDot id="export" />
      </div>
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={savePng}
          disabled={busy !== null || !ready.ok}
          title={ready.hintKey ? t(ready.hintKey) : undefined}
          className="flex-1 rounded-lg border border-hull px-3 py-1.5 text-xs text-foam transition-colors hover:border-sonar hover:bg-haze disabled:opacity-50"
        >
          {busy === 'png' ? t('map.export.preparing') : 'PNG'}
        </button>
        {seriesState.shown && (
          <button
            type="button"
            onClick={saveSeries}
            disabled={seriesState.disabled}
            title={seriesHint}
            className="flex-1 rounded-lg border border-hull px-3 py-1.5 text-xs text-foam transition-colors hover:border-sonar hover:bg-haze disabled:opacity-50"
          >
            {busy === 'series'
              ? t('map.export.seriesBusy', { done: frameAt, total: series?.frames.length ?? 0 })
              : t('map.export.series')}
          </button>
        )}
        <button
          type="button"
          onClick={saveMp4}
          disabled={busy !== null || !plan.record}
          title={mp4Hint}
          className="flex-1 rounded-lg border border-hull px-3 py-1.5 text-xs text-foam transition-colors hover:border-sonar hover:bg-haze disabled:opacity-50"
        >
          {busy === 'mp4' ? t('map.export.recording', { percent: Math.round(share * 100) }) : 'MP4'}
        </button>
      </div>
      {mp4Hint && busy === null && <div className="text-[10px] text-faint">{mp4Hint}</div>}
      {seriesHint && busy === null && <div className="text-[10px] text-faint">{seriesHint}</div>}
      {(busy === 'mp4' || busy === 'series') && (
        <>
          <div className="h-1 overflow-hidden rounded-full bg-hull">
            <div
              className="h-full bg-sonar transition-all"
              style={{ width: `${Math.round(share * 100)}%` }}
            />
          </div>
          {/* Пока идёт запись, картой лучше не двигать — иначе полёт попадёт
              в кадры. Сказать об этом дешевле, чем блокировать карту. */}
          <div className="text-[10px] text-faint">{t('map.export.holdStill')}</div>
        </>
      )}
      {ready.hintKey && busy === null && (
        <div className="text-[10px] text-buoy">{t(ready.hintKey)}</div>
      )}
      {seriesNote === 'saved' && busy === null && (
        <div className="text-[10px] text-faint">
          {t('map.export.seriesDone', { count: seriesCount })}
        </div>
      )}
      {seriesNote === 'failed' && busy === null && (
        <div className="text-[10px] text-buoy">{t('map.export.seriesFailed')}</div>
      )}
      {seriesNote === 'cancelled' && busy === null && (
        <div className="text-[10px] text-faint">{t('map.export.seriesCancelled')}</div>
      )}
      {seriesNote === 'nothing' && busy === null && (
        <div className="text-[10px] text-buoy">{t('map.export.seriesNothing')}</div>
      )}
      {askPlan && answer && <SeriesPlanDialog plan={askPlan} onChoose={answer} />}
      {note === 'no-motion' && (
        <div className="text-[10px] text-buoy">{t('map.export.nothingMoved')}</div>
      )}
      {note === null && covered && busy === null && (
        <div className="text-[10px] text-faint">{covered}</div>
      )}
      {note === null && container === 'webm' && busy === null && (
        <div className="text-[10px] text-faint">{t('map.export.savedWebm')}</div>
      )}
      {note === 'no-dates' && (
        <div className="text-[10px] text-buoy">{t('map.export.noDates')}</div>
      )}
      {note === 'unsupported' && (
        <div className="text-[10px] text-buoy">{t('map.export.mp4NoCodec')}</div>
      )}
      {note === 'failed' && (
        <div className="text-[10px] text-buoy">{t('map.export.saveFailed')}</div>
      )}
      {/* Распределение времени — техническая строка, как `detail`: `select-all`,
          чтобы её можно было выделить одним щелчком и переслать. */}
      {timing && busy === null && (
        <div className="select-all break-words text-[9px] leading-tight text-faint">
          {timing}
        </div>
      )}
      {note !== null && note !== 'no-motion' && detail && (
        <div className="select-all break-words text-[9px] leading-tight text-faint">
          {detail}
        </div>
      )}
    </div>
  )
}
